import React from "react";
import ReactDOM from "react-dom";
import { Icon, EmptyState, CompanyLogo, woBOM, notify, DataTable, useLocalPref, clickable} from "./registry";
import { newId } from "./lib/ids";
import { supplyLines } from "./lib/supplies";
import { verticalOf } from "./lib/verticals";   // the PO email goes out under the job's service brand (§13 item 18)
import { matchLines } from "./lib/stockmatch";   // receiving lands on the tracked item by SKU first (§13 item 26)
const PO_money = (n: any) => "$" + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/* the order's estimated total — per the last price list — when any line carries a unit cost; null when none does */
function poTotal(lines: any[]): number | null {
  const priced = (lines || []).filter((l: any) => l && l.unitCost != null && l.unitCost !== "" && isFinite(Number(l.unitCost)));
  if (!priced.length) return null;
  return Math.round(priced.reduce((s: number, l: any) => s + Number(l.unitCost) * (Number(l.qty) || 0), 0) * 100) / 100;
}
/* the suppliers a job's supply list names (the recipe components' supplier), named ones first, "" last when some have none */
function poSuppliersOf(bom: any[]): string[] {
  const names = Array.from(new Set((bom || []).map((b: any) => String((b && b.supplier) || "").trim())));
  return names.filter(Boolean).sort().concat(names.includes("") ? [""] : []);
}
/* the supplier's email and phone by name: Settings → Suppliers, else the legacy supplier connection */
function supplierContactOf(store: any, name: any) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return null;
  const list = store && store.settings && Array.isArray(store.settings.suppliers) ? store.settings.suppliers : [];
  const hit = list.find((x: any) => x && String(x.name || "").trim().toLowerCase() === n);
  if (hit) return { name: hit.name, email: hit.email || "", phone: hit.phone || hit.cell || "" };
  const legacy = (store && store.settings && store.settings.integrations && store.settings.integrations.supplier) || {};
  if (String(legacy.name || "").trim().toLowerCase() === n) return { name: legacy.name, email: legacy.email || "", phone: legacy.phone || "" };
  return null;
}
/* ============================================================
   Fence Works — Purchase Orders
   The materials loop, closed: a work order's BOM becomes a NUMBERED
   purchase order (PO-2026-014), printed or emailed to the supplier,
   then received against — line by line, partials included. Receiving
   bumps Inventory's on-hand counts, and the moment a job's PO is fully
   received its work order advances out of "Order Supplies" on its own.
   POs persist in settings.purchaseOrders (rides the workspace jsonb,
   same as inventory counts — no schema change).
   ============================================================ */

const poList = (settings: any) => (settings && Array.isArray(settings.purchaseOrders) ? settings.purchaseOrders : []);

/* next sequential number, per calendar year: PO-2026-001, -002, … */
function poNextNumber(settings: any) {
  const year = new Date().getFullYear();
  const re = new RegExp("^PO-" + year + "-(\\d+)$");
  const max = poList(settings).reduce((m: number, p: any) => {
    const hit = re.exec(String(p && p.number || ""));
    return hit ? Math.max(m, parseInt(hit[1], 10)) : m;
  }, 0);
  return "PO-" + year + "-" + String(max + 1).padStart(3, "0");
}

/* draft PO from a work order's estimate BOM (the same rows the crew sheet and
   the invoice show — they can never drift). */
function poFromWO(wo: any, store: any, opts: any = {}) {
  const supplierConn = (store.settings && store.settings.integrations && store.settings.integrations.supplier) || {};
  // opts.supplier: one supplier's share of the list (a recipe that buys foam
  // from one house and coating kits from another gets one PO each); "" = the
  // lines no component assigned a supplier to
  const bomAll = woBOM(wo, store) || [];
  const want = opts.supplier !== undefined ? String(opts.supplier || "").trim() : null;
  const bom = want === null ? bomAll : bomAll.filter((b: any) => String((b && b.supplier) || "").trim() === want);
  const name = want || wo.supplier || supplierConn.name || (store.settings && store.settings.defaultSupplier) || "";
  const contact = supplierContactOf(store, name);
  return {
    id: newId("po-"),
    number: "",   // minted on first save (poUpsert) — two open drafts can't collide
    status: "draft",
    supplier: name,
    supplierEmail: (contact && contact.email) || (want ? "" : supplierConn.email || ""),
    supplierPhone: (contact && contact.phone) || (want ? "" : supplierConn.phone || ""),
    workOrderId: wo.id, customer: wo.customer || "", jobAddress: wo.address || "",
    shipTo: wo.address || "",
    neededBy: wo.materialEta || "",
    notes: "",
    // the PO orders what the contractor SAID to order, not what the estimate
    // consumes — the two differ whenever they've allowed for spare or are
    // covering part of it from stock. The job's own need rides along so
    // receiving and the job card can still show the gap.
    // …each line with the SKU, the unit cost and the supplier the recipe gave it (§13 item 18)
    lines: supplyLines(bom, wo.bomOverrides).map((l: any) => { const b: any = bom.find((x: any) => x && x.label === l.label) || {}; return { label: l.label, qty: l.order, need: l.need, received: 0, ...(b.sku ? { sku: b.sku } : {}), ...(b.unitCost != null ? { unitCost: b.unitCost } : {}), ...(b.supplier ? { supplier: b.supplier } : {}) }; }),
    createdAt: Date.now(),
  };
}

/* draft PO from Inventory's current shortages (needed − on hand, across jobs) */
function poFromShortages(shorts: any[], store: any) {
  const supplierConn = (store.settings && store.settings.integrations && store.settings.integrations.supplier) || {};
  return {
    id: newId("po-"),
    number: "",   // minted on first save (poUpsert)
    status: "draft",
    supplier: supplierConn.name || (store.settings && store.settings.defaultSupplier) || "",
    supplierEmail: supplierConn.email || "",
    supplierPhone: supplierConn.phone || "",
    workOrderId: null, customer: "", jobAddress: "",
    shipTo: (store.company && store.company.address) || "Shop",
    neededBy: "", notes: "Restock — covers current shortages across open jobs.",
    lines: shorts.map((s: any) => ({ label: s.label, qty: s.short, received: 0 })),
    createdAt: Date.now(),
  };
}

/* derived status from the receiving state (canceled is sticky) */
function poDerivedStatus(po: any) {
  if (po.status === "canceled" || po.status === "draft" || po.status === "prepared") return po.status;
  const lines = po.lines || [];
  const anyRecv = lines.some((l: any) => (Number(l.received) || 0) > 0);
  const allRecv = lines.length > 0 && lines.every((l: any) => (Number(l.received) || 0) >= (Number(l.qty) || 0));
  return allRecv ? "received" : anyRecv ? "partial" : "sent";
}
const PO_STATUS: any = {
  draft:    { label: "Draft",     color: "var(--fg-3)" },
  prepared: { label: "Emailed · confirm it went out", color: "var(--amber)" },
  sent:     { label: "Ordered",   color: "#f0a35e" },
  partial:  { label: "Partial",   color: "var(--amber)" },
  received: { label: "Received",  color: "var(--green)" },
  canceled: { label: "Canceled",  color: "var(--red)" },
};

/* outstanding (ordered, not yet received) quantities per item label across
   every open PO — Inventory shows this as "on order". */
function poOnOrder(settings: any) {
  const by: any = {};
  poList(settings).forEach((po: any) => {
    const st = poDerivedStatus(po);
    if (st !== "sent" && st !== "partial") return;
    (po.lines || []).forEach((l: any) => {
      const out = Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0));
      if (out > 0) by[l.label] = (by[l.label] || 0) + out;
    });
  });
  return by;
}

/* FW-006: receiving. Each line takes at most what is still outstanding, and
   the inventory / cloud-stock movement is exactly the ACCEPTED amount — never
   the raw number typed in. Labels not on the PO are ignored. Pure. */
function poReceivePlan(po: any, deltas: Record<string, any>) {
  const accepted: Record<string, number> = {};
  const lines = (po.lines || []).map((l: any) => {
    const have = Number(l.received) || 0;
    const out = Math.max(0, (Number(l.qty) || 0) - have);
    const want = Math.max(0, Math.round(Number(deltas && deltas[l.label]) || 0));
    const d = Math.min(out, want);
    if (d <= 0) return l;
    accepted[l.label] = (accepted[l.label] || 0) + d;
    return { ...l, received: have + d };
  });
  return { lines, accepted };
}

/* persist a PO; a draft gets its sequential number HERE, at first save, from
   what's actually stored — so two drafts opened side by side never share one.
   Returns the (possibly numbered) PO. */
function poUpsert(store: any, po: any) {
  const list = poList(store.settings);
  const stored = list.find((p: any) => p.id === po.id);
  const numbered = po.number ? po : { ...po, number: (stored && stored.number) || poNextNumber(store.settings) };
  const next = stored ? list.map((p: any) => (p.id === po.id ? numbered : p)) : [numbered, ...list];
  store.setSettings({ purchaseOrders: next });
  return numbered;
}

const fmtD = (s: any) => { if (!s) return ""; const d = new Date(s + "T00:00:00"); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };

/* ============================================================
   The printable document — same cs-overlay print rails as the crew
   build sheet (window.print prints ONLY the sheet).
   ============================================================ */
function PODoc({ po, store }: any) {
  const company = store.company || {};
  const lines = po.lines || [];
  return (
    <div className="crew-sheet">
      <div className="cs-head">
        <div className="row gap-12">
          <CompanyLogo company={company} size={40} />
          <div>
            <div className="display" style={{ fontSize: 19 }}>{company.name || (store.settings && store.settings.companyName) || "Your Company"}</div>
            <div className="cs-dim mono">{[company.phone, company.address].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="display" style={{ fontSize: 21, letterSpacing: ".04em" }}>PURCHASE ORDER</div>
          <div className="mono" style={{ fontSize: 13, marginTop: 3, fontWeight: 700 }}>{po.number || "Draft — numbered on save"}</div>
          <div className="cs-dim mono" style={{ fontSize: 11.5 }}>{new Date(po.createdAt || Date.now()).toLocaleDateString()}</div>
        </div>
      </div>

      <div className="cs-job">
        <div>
          <div className="cs-kicker">Supplier</div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{po.supplier || "—"}</div>
          {po.supplierEmail && <div className="cs-dim mono">{po.supplierEmail}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="cs-kicker">Ship / deliver to</div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{po.shipTo || po.jobAddress || "—"}</div>
          <div className="cs-dim">{po.customer ? "Job: " + po.customer : "Stock order"}{po.neededBy ? " · needed by " + fmtD(po.neededBy) : ""}</div>
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14, fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #d1d5db" }}>
            <th style={{ padding: "6px 4px", width: 30 }}>#</th>
            {lines.some((l: any) => l && l.sku) && <th style={{ padding: "6px 4px", width: 110 }}>SKU</th>}
            <th style={{ padding: "6px 4px" }}>Item</th>
            <th style={{ padding: "6px 4px", textAlign: "right", width: 70 }}>Qty</th>
            {poTotal(lines) != null && <th style={{ padding: "6px 4px", textAlign: "right", width: 90 }}>Est.</th>}
            <th style={{ padding: "6px 4px", textAlign: "right", width: 80 }} className="cs-dim">Received</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l: any, i: number) => (
            <tr key={i} style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td className="cs-dim mono" style={{ padding: "6px 4px" }}>{i + 1}</td>
              {lines.some((x: any) => x && x.sku) && <td className="mono cs-dim" style={{ padding: "6px 4px" }}>{l.sku || ""}</td>}
              <td style={{ padding: "6px 4px", fontWeight: 600 }}>{l.label}</td>
              <td className="tnum" style={{ padding: "6px 4px", textAlign: "right", fontWeight: 700 }}>{l.qty}</td>
              {poTotal(lines) != null && <td className="tnum" style={{ padding: "6px 4px", textAlign: "right" }}>{l.unitCost != null && l.unitCost !== "" ? PO_money(Number(l.unitCost) * (Number(l.qty) || 0)) : ""}</td>}
              <td className="tnum cs-dim" style={{ padding: "6px 4px", textAlign: "right" }}>{(Number(l.received) || 0) > 0 ? l.received : "□"}</td>
            </tr>
          ))}
        </tbody>
        {poTotal(lines) != null && <tfoot><tr><td colSpan={lines.some((l: any) => l && l.sku) ? 4 : 3} className="cs-dim" style={{ padding: "8px 4px", textAlign: "right" }}>Estimated total · per our last price list, confirm on the acknowledgment</td><td className="tnum" style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700 }}>{PO_money(poTotal(lines))}</td><td /></tr></tfoot>}
      </table>

      {po.notes && <p style={{ fontSize: 12.5, margin: "12px 0 0", whiteSpace: "pre-wrap" }}><b>Notes:</b> {po.notes}</p>}
      <p className="cs-dim" style={{ fontSize: 11.5, margin: "14px 0 0" }}>
        Please confirm availability and delivery date. Reference <b className="mono">{po.number}</b> on all packing slips and invoices. Pricing per our supplier agreement — confirm on order acknowledgment.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 26 }}>
        <div><div style={{ borderTop: "1px solid #9ca3af", paddingTop: 4 }} className="cs-dim">Authorized by</div></div>
        <div><div style={{ borderTop: "1px solid #9ca3af", paddingTop: 4 }} className="cs-dim">Date</div></div>
      </div>
    </div>
  );
}

/* ============================================================
   Edit / send / receive modal
   ============================================================ */
function PurchaseOrderModal({ initial, store, onClose }: any) {
  const [po, setPo] = React.useState<any>(initial);
  const [saved, setSaved] = React.useState<any>(false);
  const set = (patch: any) => { setPo((p: any) => ({ ...p, ...patch })); setSaved(false); };
  const setLine = (i: number, patch: any) => set({ lines: po.lines.map((l: any, k: number) => (k === i ? { ...l, ...patch } : l)) });
  const status = poDerivedStatus(po);
  const stMeta = PO_STATUS[status] || PO_STATUS.draft;
  const wo = po.workOrderId ? (store.workOrders || []).find((w: any) => w.id === po.workOrderId) : null;

  function persist(next: any) { const saved = poUpsert(store, next); setPo(saved); setSaved(true); return saved; }

  function saveDraft() { persist(po); }

  /* FW-006: opening a mail draft proves nothing went anywhere — the composer
     can be closed, blocked, or missing. "Email supplier" prepares the draft
     (status → prepared); the PO is Ordered only when the contractor confirms
     it went out (markSent), and only then does the job flip to supplies-ordered. */
  async function sendPO() {
    if (!String(po.supplierEmail || "").trim()) { notify("Add the supplier's email first — or print the PO, place it by phone, and tap Mark as sent."); return; }
    const clean = persist({ ...po, status: po.status === "draft" ? "prepared" : po.status, preparedAt: po.preparedAt || Date.now() });   // saves first → the draft gets its number
    // the service sends it (server/src/purchasing.js — from the connected mailbox,
    // under the job's brand) and THAT is proof it went out: the PO is ordered and
    // the job flips, no "did it leave?" question. No service → the mail app, as before.
    const DB: any = (window as any).VFP_DB;
    if (DB && DB.poEmail && DB.apiBase && DB.apiBase()) {
      try {
        const r = await DB.poEmail({ po: clean, vertical: wo ? verticalOf(wo) : "fence" });
        if (r && r.ok) {
          const sent = persist({ ...clean, status: "sent", sentAt: Date.now(), sentVia: "email" });
          if (wo) store.updateWorkOrder(wo.id, { suppliesOrdered: true, supplier: sent.supplier || wo.supplier || "" });
          store.addActivity(`${sent.number} emailed to ${sent.supplierEmail}${sent.customer ? " for " + sent.customer : ""} (${(sent.lines || []).length} items)`);
          notify(`Sent to ${sent.supplierEmail}.`);
          return;
        }
      } catch (e: any) { notify(`Couldn't send it from here (${String((e && e.message) || e).slice(0, 120)}) — opening your email app instead.`, { kind: "error" }); }
    }
    const tot = poTotal(clean.lines || []);
    const lines = (clean.lines || []).map((l: any) => `• ${l.qty} × ${l.label}${l.sku ? ` (SKU ${l.sku})` : ""}${l.unitCost != null ? ` @ ${PO_money(l.unitCost)}` : ""}`).join("\n") + (tot != null ? `\n\nEstimated total: ${PO_money(tot)} (per our last price list — confirm on the acknowledgment)` : "");
    const subj = encodeURIComponent(`${clean.number} — material order${clean.customer ? " · " + clean.customer : ""}`);
    const body = encodeURIComponent(
      `Purchase order ${clean.number}\n\n${lines}\n\nDeliver to: ${clean.shipTo || clean.jobAddress || "TBD"}` +
      (clean.neededBy ? `\nNeeded by: ${fmtD(clean.neededBy)}` : "\nNeeded by: as soon as possible — please confirm delivery date.") +
      (clean.notes ? `\n\nNotes: ${clean.notes}` : "") +
      `\n\nPlease reference ${clean.number} on the packing slip & invoice.\n\nThanks,\n${(store.settings && store.settings.companyName) || (store.company && store.company.name) || "Fence Works"}`);
    window.open(`mailto:${clean.supplierEmail || ""}?subject=${subj}&body=${body}`, "_blank");
  }
  /* most yards take orders by text. Same lines, same PO number, one sms: hand-off;
     "Mark as sent" afterwards is what flips the job, exactly as with email. */
  function sendPOText() {
    const phone = String(po.supplierPhone || "").replace(/[^+\d]/g, "");
    if (!phone) { notify("Add the supplier's cell number on the Suppliers tab first — or call it in and tap Mark as sent."); return; }
    const clean = persist({ ...po, status: po.status === "draft" ? "prepared" : po.status, preparedAt: po.preparedAt || Date.now() });
    const lines = (clean.lines || []).map((l: any) => `${l.qty} x ${l.label}${l.sku ? ` (${l.sku})` : ""}`).join("\n");
    const co = (store.settings && store.settings.companyName) || (store.company && store.company.name) || "Fence Works";
    const body = `${co} — PO ${clean.number}\n${lines}\nDeliver to: ${clean.shipTo || clean.jobAddress || "TBD"}\nNeeded by: ${clean.neededBy ? fmtD(clean.neededBy) : "ASAP — please confirm"}\nReply YES to confirm. Ref ${clean.number} on the slip.`;
    window.open(`sms:${phone}?&body=${encodeURIComponent(body)}`, "_blank");
  }
  function markSent(via: string) {
    if (!(po.lines || []).length) { notify("Add at least one line before marking the order placed."); return; }
    const clean = persist({ ...po, status: (po.status === "draft" || po.status === "prepared") ? "sent" : po.status, sentAt: po.sentAt || Date.now(), sentVia: via });
    if (wo) store.updateWorkOrder(wo.id, { suppliesOrdered: true, supplier: clean.supplier || wo.supplier || "" });
    store.addActivity(`${clean.number} placed with ${clean.supplier || "supplier"}${clean.customer ? " for " + clean.customer : ""} (${(clean.lines || []).length} items · ${via})`);
  }

  /* receive a delta on one line (or everything outstanding): updates the PO,
     bumps Inventory on-hand by exactly what just arrived, and — when the PO is
     now complete and its job still sits in "Order Supplies" — advances the
     work order to Ready to Schedule. One setSettings call so the PO list and
     inventory counts can't race each other. */
  function receive(deltas: Record<string, number>) {
    const plan = poReceivePlan(po, deltas);
    const got = Object.entries(plan.accepted).filter(([, d]: any) => d > 0);
    if (!got.length) { notify("Nothing to receive — those lines are already in."); return; }
    const next = { ...po, number: po.number || poNextNumber(store.settings), lines: plan.lines, status: (po.status === "draft" || po.status === "prepared") ? "sent" : po.status };
    const nowSt = poDerivedStatus(next);
    if (nowSt === "received" && !next.receivedAt) next.receivedAt = Date.now();

    const inv = { ...((store.settings && store.settings.inventory) || {}) };
    got.forEach(([label, d]: any) => {
      const rec = { ...(inv[label] || {}) };
      rec.onHand = Math.max(0, Math.round((Number(rec.onHand) || 0) + d));
      inv[label] = rec;
    });
    const list = poList(store.settings);
    const nextList = list.some((p: any) => p.id === next.id) ? list.map((p: any) => (p.id === next.id ? next : p)) : [next, ...list];
    store.setSettings({ purchaseOrders: nextList, inventory: inv });
    setPo(next); setSaved(true);

    store.addActivity(`${next.number}: received ${got.map(([l, d]: any) => `${d} × ${l}`).join(", ")} — inventory updated`);
    // cloud-inventory bridge: label-matched items get a real ledger "receive"
    // (best-effort — items not tracked in the cloud yet just use the counts)
    try {
      const ST: any = (window as any).VFP_STOCK;
      if (ST && ST.enabled()) got.forEach(([label, d]: any) => {
        // by SKU first (lib/stockmatch.ts): the supplier's number on the line finds the item however it's named in Inventory
        const line = (next.lines || []).find((l: any) => l && l.label === label);
        const p = line && line.sku && typeof ST.receiveLine === "function" ? ST.receiveLine({ sku: line.sku, label }, d, next.number) : ST.receiveByLabel(label, d, next.number);
        Promise.resolve(p).catch(() => {});
      });
    } catch (e) { /* never let the bridge break receiving */ }
    if (nowSt === "received" && wo && wo.status === "materials") {
      store.updateWorkOrder(wo.id, { status: "unscheduled", suppliesOrdered: true });
      store.addActivity(`${wo.customer || "Job"}: all materials in (${next.number}) — moved to Ready to Schedule`);
    }
  }

  const outstanding = (po.lines || []).reduce((s: number, l: any) => s + Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0)), 0);
  const [recvDraft, setRecvDraft] = React.useState<any>({});
  // where each line lands in cloud Inventory (by SKU, then label, then name) — read once the order can be received
  const [stockItems, setStockItems] = React.useState<any[] | null>(null);
  const receivable = status !== "draft" && status !== "prepared" && status !== "canceled";
  React.useEffect(() => {
    if (!receivable) return;
    let dead = false;
    try {
      const ST: any = (window as any).VFP_STOCK;
      if (ST && ST.enabled()) Promise.resolve().then(() => ST.items()).then((rows: any[]) => { if (!dead) setStockItems(rows || []); }).catch(() => { if (!dead) setStockItems([]); });
    } catch (e) { /* no cloud stock — the counts still move */ }
    return () => { dead = true; };
  }, [receivable]);
  const landing = React.useMemo(() => matchLines(stockItems, po.lines), [stockItems, po.lines]);

  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}>
            <Icon name="box" size={14} className="muted" />{po.number || "New purchase order"}
            <span className="chip" style={{ fontSize: 10.5, color: stMeta.color, borderColor: "var(--line-2)" }}>{stMeta.label}</span>
          </span>
          <div className="row gap-8">
            <button className="btn btn-ghost btn-sm" onClick={saveDraft}><Icon name="check" size={13} />{saved ? "Saved ✓" : "Save"}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { if (!po.number) persist(po); setTimeout(() => window.print(), 0); }}><Icon name="printer" size={13} />Print / PDF</button>
            {(status === "draft" || status === "prepared")
              ? <>
                  <button className="btn btn-outline btn-sm" onClick={sendPO}><Icon name="send" size={13} />{status === "prepared" ? "Open the email again" : "Email supplier"}</button>
                  <button className="btn btn-outline btn-sm" onClick={sendPOText} title="Text the order to the yard's cell"><Icon name="chat" size={13} />Text the order</button>
                  <button className="btn btn-primary btn-sm" onClick={() => markSent(status === "prepared" ? "email" : "phone/in person")}><Icon name="check" size={13} />Mark as sent</button>
                </>
              : <button className="btn btn-ghost btn-sm" onClick={sendPO}><Icon name="send" size={13} />Re-send email</button>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll">
          {/* ---- edit strip (screen only) ---- */}
          <div className="no-print" style={{ display: "grid", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)" }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 160px" }}><label className="field-label">Supplier</label>
                <input className="input" value={po.supplier || ""} placeholder="e.g. Midwest Fence Supply" onChange={(e) => set({ supplier: e.target.value })} /></div>
              <div style={{ flex: "1 1 180px" }}><label className="field-label">Supplier email</label>
                <input className="input" value={po.supplierEmail || ""} placeholder="orders@…" onChange={(e) => set({ supplierEmail: e.target.value })} /></div>
              <div style={{ flex: "0 1 140px" }}><label className="field-label">Needed by</label>
                <input className="input" type="date" value={po.neededBy || ""} onChange={(e) => set({ neededBy: e.target.value })} /></div>
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px" }}><label className="field-label">Ship / deliver to</label>
                <input className="input" value={po.shipTo || ""} placeholder="Job site or shop address" onChange={(e) => set({ shipTo: e.target.value })} /></div>
              <div style={{ flex: "1 1 220px" }}><label className="field-label">Notes to supplier</label>
                <input className="input" value={po.notes || ""} placeholder="Gate hardware in black, call before delivery…" onChange={(e) => set({ notes: e.target.value })} /></div>
            </div>
            {/* line editor: qty tweaks + add/remove */}
            <div>
              <div className="row between" style={{ marginBottom: 6 }}>
                <span className="kicker">Lines · {po.lines.length}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => set({ lines: [...po.lines, { label: "", qty: 1, received: 0 }] })}><Icon name="plus" size={12} />Add line</button>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {po.lines.map((l: any, i: number) => (
                  <div key={i} className="row gap-8" style={{ flexWrap: "wrap" }}>
                    <input className="input" style={{ flex: "2 1 200px", minWidth: 0 }} value={l.label} placeholder="Item" onChange={(e) => setLine(i, { label: e.target.value })} />
                    <input className="input mono" style={{ flex: "0 1 110px" }} value={l.sku || ""} placeholder="SKU" title="The supplier's item number (from the recipe)" onChange={(e) => setLine(i, { sku: e.target.value })} />
                    <input className="input tnum" type="number" min="0" style={{ width: 76 }} value={l.qty} onChange={(e) => setLine(i, { qty: Math.max(0, Math.round(+e.target.value || 0)) })} />
                    <input className="input tnum" type="number" min="0" step="0.01" style={{ width: 92 }} value={l.unitCost == null ? "" : l.unitCost} placeholder="$/unit" title="Unit cost per the last price list" onChange={(e) => setLine(i, { unitCost: e.target.value === "" ? null : Math.max(0, +e.target.value || 0) })} />
                    <span className="tnum muted" style={{ width: 84, textAlign: "right", fontSize: 12.5 }}>{l.unitCost != null && l.unitCost !== "" ? PO_money(Number(l.unitCost) * (Number(l.qty) || 0)) : ""}</span>
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => set({ lines: po.lines.filter((_: any, k: number) => k !== i) })}><Icon name="x" size={13} /></button>
                  </div>
                ))}
                {poTotal(po.lines) != null && <div className="row between" style={{ marginTop: 4, fontSize: 13 }}><span className="muted">Estimated total · per the last price list, confirm on the acknowledgment</span><b className="tnum">{PO_money(poTotal(po.lines))}</b></div>}
              </div>
            </div>
            {status === "prepared" && (
              <div className="pe-callout info" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <p className="muted" style={{ margin: 0, fontSize: 12.5, flex: "1 1 240px" }}>The order was opened in your email app{po.preparedAt ? " " + new Date(po.preparedAt).toLocaleString() : ""}. It only counts as ordered once you say it went out.</p>
                <button className="btn btn-primary btn-sm" onClick={() => markSent("email")}><Icon name="check" size={13} />Yes — it went out</button>
              </div>
            )}
            {/* ---- receiving (once it's ordered) ---- */}
            {status !== "draft" && status !== "prepared" && status !== "canceled" && (
              <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px", background: "var(--surface-2)" }}>
                <div className="row between" style={{ marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <span className="kicker">Receiving — check in what arrived</span>
                  {outstanding > 0
                    ? <button className="btn btn-outline btn-sm" onClick={() => { const all: any = {}; po.lines.forEach((l: any) => { const out = Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0)); if (out) all[l.label] = out; }); receive(all); setRecvDraft({}); }}>
                        <Icon name="check" size={13} />Receive all remaining ({outstanding})</button>
                    : <span className="chip green" style={{ fontSize: 10.5 }}><Icon name="check" size={11} />Fully received</span>}
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  {po.lines.map((l: any, i: number) => {
                    const out = Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0));
                    return (
                      <div key={i} className="row gap-8" style={{ fontSize: 12.5, alignItems: "center" }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</span>
                          {landing[l.label]
                            ? <span className="faint" style={{ display: "block", fontSize: 10.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={landing[l.label]!.by === "sku" ? `Matched to the Inventory item by SKU ${l.sku}` : "Matched to the Inventory item by its name"}>→ {landing[l.label]!.item.name}{landing[l.label]!.by === "sku" ? ` · SKU ${l.sku}` : ""}</span>
                            : (stockItems && l.sku ? <span className="faint" style={{ display: "block", fontSize: 10.5 }}>no tracked item carries SKU {l.sku} — counts only</span> : null)}
                        </span>
                        <span className="tnum muted" style={{ flexShrink: 0 }}>{Number(l.received) || 0} / {l.qty} in</span>
                        {out > 0 && <>
                          <input className="input tnum" type="number" min="0" max={out} placeholder={"≤" + out} style={{ width: 74 }}
                            value={recvDraft[l.label] != null ? recvDraft[l.label] : ""}
                            onChange={(e) => setRecvDraft((d: any) => ({ ...d, [l.label]: e.target.value }))} />
                          <button className="btn btn-ghost btn-sm" onClick={() => { const d = Math.min(out, Math.max(0, Math.round(+recvDraft[l.label] || 0) || out)); receive({ [l.label]: d }); setRecvDraft((dd: any) => { const n = { ...dd }; delete n[l.label]; return n; }); }}>
                            <Icon name="check" size={12} />In</button>
                        </>}
                      </div>
                    );
                  })}
                </div>
                <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Received counts land on Inventory's on-hand automatically — on the item with the line's SKU first, then the one with its name{wo ? "; when everything's in, the job moves out of Order Supplies on its own" : ""}.</p>
              </div>
            )}
          </div>
          {/* ---- the printable document ---- */}
          <PODoc po={po} store={store} />
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ============================================================
   The Purchase Orders page — every PO, filterable, one tap to open.
   ============================================================ */
function PurchaseOrdersPage({ store, go }: any) {
  const pos = poList(store.settings);
  const [filter, setFilter] = React.useState<any>("open");
  const [openPo, setOpenPo] = React.useState<any>(null);
  const [view, setView] = useLocalPref("fw_po_view", "list");   // cards | list — remembered per device
  const withStatus = pos.map((p: any) => ({ ...p, _st: poDerivedStatus(p) }));
  const isOpen = (st: string) => st === "sent" || st === "partial" || st === "draft" || st === "prepared";
  const counts: any = { all: pos.length, open: withStatus.filter((p: any) => isOpen(p._st)).length };
  ["draft", "sent", "partial", "received"].forEach((s) => { counts[s] = withStatus.filter((p: any) => p._st === s || (s === "draft" && p._st === "prepared")).length; });
  const list = withStatus.filter((p: any) =>
    filter === "all" ? true :
    filter === "open" ? isOpen(p._st) :
    filter === "draft" ? (p._st === "draft" || p._st === "prepared") :
    p._st === filter);

  return (
    <div className="page fade-up" style={{ maxWidth: 1000 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Purchase Orders</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Numbered, printable, and received-against — materials stop living in your head.</p></div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <div className="seg view-seg" role="tablist" aria-label="View">
            {[["cards", "Cards"], ["list", "List"]].map(([v, lb]) => (
              <button key={v} className={"seg-btn" + (view === v ? " on" : "")} onClick={() => setView(v)}>{lb}</button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => go("inventory")}><Icon name="box" size={15} />Inventory</button>
          <button className="btn btn-primary" onClick={() => setOpenPo(poFromShortages([], store))}><Icon name="plus" size={15} />New PO</button>
        </div>
      </div>

      <div className="cat-tabs" style={{ marginBottom: 14 }}>
        {[["open", "Open"], ["all", "All"], ["draft", "Draft"], ["sent", "Ordered"], ["partial", "Partial"], ["received", "Received"]].map(([id, lb]: any) => (
          <button key={id} className={"filter-chip" + (filter === id ? " on" : "")} onClick={() => setFilter(id)}>{lb} · {counts[id] || 0}</button>
        ))}
      </div>

      <div className={"card" + (view === "list" && list.length ? "" : " pad")} style={view === "list" && list.length ? { padding: "6px 14px 8px" } : undefined}>
        {list.length === 0
          ? <EmptyState icon="box" title="No purchase orders here" sub='Create one from a job in Work Orders ("Create purchase order" in the Order Supplies step) or from Inventory shortages.' action={{ label: "Go to Work Orders", icon: "clipboard", onClick: () => go("workorders") }} secondary={{ label: "Inventory shortages", onClick: () => go("inventory") }} />
          : view === "list"
          ? <DataTable rowKey="id" onRowClick={(p: any) => setOpenPo(p)}
              rows={list.map((p: any) => ({ ...p, _items: (p.lines || []).length, _out: (p.lines || []).reduce((s: number, l: any) => s + Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0)), 0) }))}
              columns={[
                { key: "number", label: "Number", render: (p: any) => p.number ? <span className="mono" style={{ fontWeight: 700 }}>{p.number}</span> : <span className="chip" style={{ fontSize: 10.5 }}>Draft</span> },
                { key: "supplier", label: "Supplier", render: (p: any) => p.supplier || <span className="muted">No supplier</span> },
                { key: "customer", label: "Job", className: "ellipsis", render: (p: any) => p.customer || <span className="muted">Stock</span> },
                { key: "_items", label: "Items", num: true },
                { key: "_out", label: "Outstanding", num: true, render: (p: any) => p._out > 0 && p._st !== "draft" ? <span style={{ color: "#f0a35e", fontWeight: 700 }}>{p._out}</span> : <span className="muted">—</span> },
                { key: "neededBy", label: "Needed by", render: (p: any) => p.neededBy ? fmtD(p.neededBy) : <span className="muted">—</span> },
                { key: "_st", label: "Status", render: (p: any) => { const st = PO_STATUS[p._st] || PO_STATUS.draft; return <span className="chip" style={{ fontSize: 10.5, color: st.color, borderColor: "var(--line-2)" }}><span className="wo-pipe-dot" style={{ background: st.color }} />{st.label}</span>; } },
              ]} />
          : <div style={{ display: "grid", gap: 9 }}>
              {list.map((p: any) => {
                const st = PO_STATUS[p._st] || PO_STATUS.draft;
                const items = (p.lines || []).length;
                const out = (p.lines || []).reduce((s: number, l: any) => s + Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0)), 0);
                return (
                  <div key={p.id} className="proj-row" {...clickable(() => setOpenPo(p), { label: `Open purchase order ${p.number || ""}` })}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5 }} className="mono">{p.number} <span style={{ fontWeight: 500, fontFamily: "inherit" }}>· {p.supplier || "No supplier"}</span></div>
                      <div className="muted" style={{ fontSize: 12.5 }}>{p.customer ? p.customer + " · " : "Stock · "}{items} item{items === 1 ? "" : "s"}{out > 0 && p._st !== "draft" ? ` · ${out} outstanding` : ""}{p.neededBy ? " · need " + fmtD(p.neededBy) : ""}</div>
                    </div>
                    <span className="chip" style={{ fontSize: 10.5, color: st.color, borderColor: "var(--line-2)" }}><span className="wo-pipe-dot" style={{ background: st.color }} />{st.label}</span>
                    <Icon name="chevright" size={16} className="muted" />
                  </div>
                );
              })}
            </div>}
      </div>
      {openPo && <PurchaseOrderModal initial={openPo} store={store} onClose={() => setOpenPo(null)} />}
    </div>
  );
}

Object.assign(window, { PurchaseOrdersPage, PurchaseOrderModal });

export { PurchaseOrdersPage, PurchaseOrderModal, poFromWO, poFromShortages, poList, poDerivedStatus, poReceivePlan, poOnOrder, poNextNumber, PO_STATUS, poSuppliersOf, supplierContactOf, poTotal };
